<template>
  <div>
    <risk-protection-sidebar />
    <!-- Filters -->
    <b-card class="financial-planning-step-3">
      <b-card-title>
        <div class="financial-step-title m-1">
          <div class="m-1">
            <h4>Gestão de riscos</h4>
            <span class="text-muted h5">Passo 03</span>
          </div>
        </div>
      </b-card-title>
      <template v-if="loading.form">
        <b-row class="justify-content-md-center mt-2">
          <b-spinner big variant="primary" />
        </b-row>
      </template>
      <b-card-body v-else>
        <!-- Copiloto de IA — sugestão de gestão de risco (Passo 3 / crawler Azos) -->
        <b-row class="mb-2">
          <b-col cols="12">
            <b-button
              v-if="!riskSuggestion"
              size="sm"
              variant="outline-primary"
              :disabled="copilot.loading"
              @click="generateRiskSuggestion"
            >
              <b-spinner v-if="copilot.loading" small class="mr-50" />
              <feather-icon v-else icon="ZapIcon" class="mr-50" />
              {{
                copilot.loading
                  ? "Gerando sugestão... (a cotação pode levar alguns minutos)"
                  : "Gerar sugestão de IA"
              }}
            </b-button>

            <b-card v-if="riskSuggestion" no-body class="copilot-card border mt-1">
              <div class="px-1 pt-1 d-flex align-items-center">
                <feather-icon icon="ZapIcon" class="text-primary mr-50" />
                <small class="font-weight-bold text-primary">
                  Sugestão de IA (apoio — revise antes de aplicar)
                </small>
              </div>
              <div class="px-1 pb-1">
                <p class="mb-50 mt-1"><b>Necessidades de proteção</b></p>
                <div class="mb-1">
                  <b-badge
                    v-for="need in suggestedNeeds"
                    :key="need"
                    variant="light-primary"
                    class="mr-50 mb-50"
                  >{{ need }}</b-badge>
                  <span v-if="!suggestedNeeds.length" class="text-muted">Nenhuma</span>
                </div>

                <b-row class="mb-1">
                  <b-col cols="6" md="3">
                    <small class="text-muted">Gastos mensais</small><br />
                    {{ riskSuggestion.renda_passiva.somatoria_gastos_mensais | toCurrency }}
                  </b-col>
                  <b-col cols="6" md="3">
                    <small class="text-muted">Indenização morte</small><br />
                    {{ riskSuggestion.renda_passiva.indenizacao_morte_necessaria | toCurrency }}
                  </b-col>
                  <b-col cols="6" md="3">
                    <small class="text-muted">Custo proteções</small><br />
                    {{ riskSuggestion.renda_passiva.custos_novas_protecoes | toCurrency }}
                  </b-col>
                  <b-col cols="6" md="3">
                    <small class="text-muted">Nova poupança</small><br />
                    {{ riskSuggestion.renda_passiva.nova_capacidade_poupanca | toCurrency }}
                  </b-col>
                </b-row>

                <p class="mb-50">
                  <b>Proteções sugeridas</b>
                  <small class="text-muted">(referência — adicione na tabela abaixo)</small>
                </p>
                <ul class="pl-2 mb-1">
                  <li
                    v-for="(p, i) in riskSuggestion.protecao_detalhada"
                    :key="i"
                  >
                    {{ p.produto_necessidade }} — {{ p.seguradora }}
                    <template v-if="p.coberturas && p.coberturas.length">
                      ({{ p.coberturas.join(", ") }})
                    </template>
                    · Indeniz.: {{ p.indenizacao | toCurrency }} · Prêmio:
                    {{ p.premio | toCurrency }}
                  </li>
                  <li
                    v-if="!riskSuggestion.protecao_detalhada.length"
                    class="text-muted"
                  >
                    Nenhuma
                  </li>
                </ul>

                <!-- Campo de motivo da rejeição -->
                <b-form-input
                  v-if="copilot.rejecting"
                  v-model="riskRejectionReason"
                  placeholder="Motivo da rejeição (opcional)"
                  class="mb-1"
                />

                <div class="d-flex flex-wrap" style="gap: 0.5rem">
                  <template v-if="!copilot.rejecting">
                    <b-button
                      size="sm"
                      variant="success"
                      :disabled="copilot.busy"
                      @click="acceptRiskSuggestion"
                    >
                      Aceitar
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      :disabled="copilot.busy"
                      @click="rejectRiskSuggestion"
                    >
                      Rejeitar
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      :disabled="copilot.busy"
                      @click="regenerateRiskSuggestion"
                    >
                      <b-spinner v-if="copilot.busy" small />
                      <span v-else>Nova sugestão</span>
                    </b-button>
                  </template>

                  <template v-else>
                    <b-button
                      size="sm"
                      variant="danger"
                      :disabled="copilot.busy"
                      @click="confirmRejectRisk"
                    >
                      Confirmar rejeição
                    </b-button>
                    <b-button
                      size="sm"
                      variant="flat-secondary"
                      :disabled="copilot.busy"
                      @click="cancelRejectRisk"
                    >
                      Cancelar
                    </b-button>
                  </template>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="protection-needs">
          <b-col cols="12" class="mb-1">
            <span
              >Escolha entre as opções abaixo, quais seriam as necessidades de
              proteção do cliente?</span
            >
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox v-model="health_monitoring" @change="saveData">
              <span>Monitorar a saúde</span>
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox v-model="health_recovery" @change="saveData">
              <span>Recuperar a saúde</span>
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox
              v-model="professional_force_protection"
              @change="saveData"
            >
              <span>Proteger a força de trabalho</span>
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox v-model="dependent_coverage" @change="saveData">
              <span>Garantir os dependentes</span>
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox
              v-model="professional_liabilities"
              @change="saveData"
            >
              <span>Responsabilidade profissional</span>
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox
              v-model="real_estate_protection"
              @change="saveData"
            >
              <span>Proteção de bens (imóvel)</span>
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-checkbox
              v-model="high_value_asset_protection"
              @change="saveData"
            >
              <span>Proteção de bens (alto valor)</span>
            </b-form-checkbox>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" class="mb-2">
            <div class="boder-col"></div>
          </b-col>
        </b-row>
        <b-row class="mb-1">
          <b-col cols="12" md="6">
            <span>Renda passiva emergencial</span>
          </b-col>
          <b-col cols="12" md="6">
            <span>Novo valor de poupar</span>
          </b-col>
        </b-row>
        <b-row class="mb-1">
          <b-col cols="12" md="6">
            <b-form-group
              label="Somatória gastos mensais clientes"
              label-for="month-expenses"
              label-size="sm"
            >
              <b-form-input
                id="month-expenses"
                v-model="monthly_expenses_sum"
                v-money="moneyFormat"
                @blur="saveData"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              label="O custos com as novas proteções"
              label-for="protections-costs"
              label-size="sm"
            >
              <b-form-input
                id="protections-costs"
                v-model="cost_of_new_protections"
                v-money="moneyFormat"
                @blur="saveData"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              label="Indenização de morte necessária é:"
              label-for="death-indemnity"
              label-size="sm"
            >
              <b-form-input
                id="month-expenses"
                v-model="death_indemnity"
                v-money="moneyFormat"
                @blur="saveData"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              label="Nova capacidade de poupança do cliente:"
              label-for="savings-capability"
              label-size="sm"
            >
              <b-form-input
                id="savings-capability"
                v-model="new_client_savings_amount"
                v-money="moneyFormat"
                @blur="saveData"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="align-items-center">
          <b-col cols="12" class="mb-2 mt-2">
            <div class="boder-col"></div>
          </b-col>
          <b-col cols="12" md="6" class="mb-1">
            <span>Proteção detalhada</span>
          </b-col>
          <b-col cols="12" md="6" class="mb-1 add-new-protection">
            <b-button variant="success" @click="editProtection(undefined)">
              Adicionar
            </b-button>
          </b-col>
        </b-row>
        <template v-if="loading.table">
          <b-row class="justify-content-md-center mt-2">
            <b-spinner big variant="primary" />
          </b-row>
        </template>
        <b-row v-else>
          <b-col cols="12" md="12" class="mb-4" style="overflow: overlay">
            <table class="financial-planning-table">
              <thead>
                <tr>
                  <th width="30%">Produto/necessidade de proteção</th>
                  <th width="30%">Seguradora/cobertura</th>
                  <th width="15%">Indenização</th>
                  <th width="15%">Prêmio</th>
                  <th width="10%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="protection in riskProtections">
                  <td>
                    <div class="d-flex justify-content-start">
                      <div class="first-column-icon">
                        <feather-icon
                          icon="ShieldIcon"
                          size="25"
                          style="position: absolute"
                        />
                        <img
                          v-if="skin == 'dark'"
                          :src="
                            require('@/assets/images/svg/icn_circle_planning_table-dark.svg')
                          "
                          width="40px"
                        />
                        <img
                          v-else
                          :src="
                            require('@/assets/images/svg/icn_circle_planning_table.svg')
                          "
                          width="40px"
                        />
                      </div>
                      <div class="first-column-text">
                        <b>{{ protection.product }}</b
                        ><br />
                        {{ protection.need }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <b>{{ protection.company }}</b
                    ><br />
                    <template v-for="(coverage, index) in protection.coverages">
                      {{
                        index < protection.coverages.length - 1
                          ? coverage.name + ","
                          : coverage.name
                      }}
                    </template>
                  </td>
                  <td>
                    {{ protection.compensation | toCurrency }}
                  </td>
                  <td>
                    {{ protection.award | toCurrency }}
                  </td>
                  <td>
                    <b-button
                      variant="link"
                      @click="editProtection(protection.id)"
                    >
                      <feather-icon icon="Edit3Icon" size="16" />
                    </b-button>
                    <b-button
                      variant="link"
                      @click="deleteProtection(protection.id)"
                    >
                      <feather-icon icon="Trash2Icon" size="16" />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
        <div class="financial-previous-step">
          <b-button variant="outline-primary" @click="toStep('previous')">
            Voltar
          </b-button>
        </div>
        <div class="financial-next-step">
          <b-button variant="primary" @click="toStep('next')">
            Próximo
          </b-button>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
} from "bootstrap-vue";
import _ from "lodash";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import RiskProtectionSidebar from "../components/RiskProtectionSidebar";
import useVuelidate from "@vuelidate/core";
import { floatToMoney, moneyToFloat, toCurrency } from "@/helpers/converters";
import { VMoney } from "v-money";
import { getBRLFormat } from "@/helpers/formatting";
import * as types from "../store/types";
import useAppConfig from "@core/app-config/useAppConfig";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";

// Necessidades vindas da IA (passo3/all) → checkboxes deste passo.
const NEED_MAP = {
  monitorar_recuperar_saude: "health_monitoring",
  recuperar_saude: "health_recovery",
  proteger_forca_trabalho: "professional_force_protection",
  garantir_dependentes: "dependent_coverage",
  responsabilidade_profissional: "professional_liabilities",
  protecao_bens_imovel: "real_estate_protection",
  protecao_bens_alto_valor: "high_value_asset_protection",
};

// Rótulos para exibir as necessidades sugeridas no card.
const NEED_LABELS = {
  monitorar_recuperar_saude: "Monitorar a saúde",
  recuperar_saude: "Recuperar a saúde",
  proteger_forca_trabalho: "Proteger a força de trabalho",
  garantir_dependentes: "Garantir os dependentes",
  responsabilidade_profissional: "Responsabilidade profissional",
  protecao_bens_imovel: "Proteção de bens (imóvel)",
  protecao_bens_alto_valor: "Proteção de bens (alto valor)",
};

// Polling da geração assíncrona: o backend enfileira a cotação (crawler) e o
// front acompanha a recomendação `processing` até virar `presented`/erro.
// A janela precisa cobrir os até ~10 min que o steps_agents espera o crawler.
const COPILOT_POLL_INTERVAL_MS = 5000;
const COPILOT_POLL_MAX_ATTEMPTS = 140;

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    vSelect,
    RiskProtectionSidebar,
  },
  props: {
    toStep: Function,
    customerToken: String,
  },
  directives: {
    money: VMoney,
  },
  setup() {
    const { skin } = useAppConfig();
    return { toast: useToast(), skin, v$: useVuelidate() };
  },
  data() {
    return {
      loading: {
        form: false,
        table: false,
      },
      health_monitoring: undefined,
      dependent_coverage: undefined,
      high_value_asset_protection: undefined,
      health_recovery: undefined,
      professional_liabilities: undefined,
      professional_force_protection: undefined,
      real_estate_protection: undefined,
      monthly_expenses_sum: undefined,
      death_indemnity: undefined,
      cost_of_new_protections: undefined,
      new_client_savings_amount: undefined,
      // Copiloto (Passo 3)
      copilot: { loading: false, busy: false, rejecting: false },
      riskSuggestion: undefined,
      riskRecommendationId: undefined,
      riskRejectionReason: "",
      copilotPollTimer: undefined,
    };
  },
  computed: {
    ...mapGetters({
      riskProtections: types.RISK_PROTECTIONS,
    }),
    moneyFormat: function () {
      return getBRLFormat();
    },
    suggestedNeeds() {
      if (!this.riskSuggestion || !this.riskSuggestion.necessidades) return [];
      return Object.keys(NEED_LABELS)
        .filter((k) => this.isTrue(this.riskSuggestion.necessidades[k]))
        .map((k) => NEED_LABELS[k]);
    },
  },
  mounted() {
    this.loading.form = true;
    this.getRisks(this.$route.params.id)
      .then((response) => {
        if (response.data) {
          this.health_monitoring = Boolean(response.data.health_monitoring);
          this.dependent_coverage = Boolean(response.data.dependent_coverage);
          this.high_value_asset_protection = Boolean(
            response.data.high_value_asset_protection
          );
          this.health_recovery = Boolean(response.data.health_recovery);
          this.professional_liabilities = Boolean(
            response.data.professional_liabilities
          );
          this.professional_force_protection = Boolean(
            response.data.professional_force_protection
          );
          this.real_estate_protection = Boolean(
            response.data.real_estate_protection
          );
          this.monthly_expenses_sum = floatToMoney(
            response.data.monthly_expenses_sum
          );
          this.death_indemnity = floatToMoney(response.data.death_indemnity);
          this.cost_of_new_protections = floatToMoney(
            response.data.cost_of_new_protections
          );
          this.new_client_savings_amount = floatToMoney(
            response.data.new_client_savings_amount
          );
        }
      })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar as informações. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.form = false;
      });
    this.getProtections();
  },
  beforeDestroy() {
    // A recomendação segue processando no backend; ao voltar e clicar em gerar,
    // a linha `processing` é reaproveitada e o acompanhamento continua.
    clearTimeout(this.copilotPollTimer);
  },
  methods: {
    ...mapActions({
      getRisks: types.GET_RISKS,
      saveRisks: types.SAVE_RISKS,
      getRisksProtections: types.GET_RISKS_PROTECTIONS,
      removeRiskProtection: types.REMOVE_RISK_PROTECTION,
      openRiskProtectionSidebar: types.OPEN_RISK_PROTECTION_SIDEBAR,
      generateCopilot: types.GENERATE_COPILOT_SUGGESTION,
      getCopilotRecommendation: types.GET_COPILOT_RECOMMENDATION,
      decideCopilot: types.DECIDE_COPILOT_SUGGESTION,
    }),
    // ---- Copiloto (Passo 3) ----
    isTrue(value) {
      return value === 1 || value === true || value === "1";
    },
    parseSuggestion(recommendation) {
      try {
        const data =
          typeof recommendation.original_content === "string"
            ? JSON.parse(recommendation.original_content)
            : recommendation.original_content;
        if (!data || !data.necessidades) return undefined;
        data.renda_passiva = data.renda_passiva || {};
        data.protecao_detalhada = data.protecao_detalhada || [];
        return data;
      } catch (e) {
        return undefined;
      }
    },
    notifyError(text, timeout = 3000) {
      this.toast(
        {
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        },
        { timeout }
      );
    },
    resetSuggestion() {
      this.riskSuggestion = undefined;
      this.riskRecommendationId = undefined;
      this.copilot.rejecting = false;
      this.riskRejectionReason = "";
    },
    generateRiskSuggestion() {
      this.copilot.loading = true;
      this.generateCopilot({
        customer_token: this.customerToken,
        field_key: "risk_protection",
      })
        .then((resp) => this.handleRecommendation(resp.data))
        .catch((err) => {
          this.copilot.loading = false;
          const unavailable = err?.response?.status === 503;
          this.notifyError(
            unavailable
              ? "O Copiloto está indisponível agora. Preencha manualmente."
              : "Não foi possível gerar a sugestão de IA. Tente novamente."
          );
        });
    },
    // Recebe uma recomendação em qualquer estado: `processing` → acompanha via
    // polling; `presented` → renderiza o card; erro → avisa e libera os botões.
    handleRecommendation(recommendation) {
      if (recommendation.status === "processing") {
        this.pollRecommendation(recommendation.id, 0);
        return;
      }
      this.copilot.loading = false;
      this.copilot.busy = false;
      const hasTechnicalError =
        ["processing_error", "error"].includes(recommendation.status) ||
        recommendation.technical_status === "error";
      if (hasTechnicalError) {
        this.notifyError(
          "O serviço de cotação está temporariamente indisponível. Tente novamente mais tarde ou preencha esta etapa manualmente.",
          8000
        );
        return;
      }
      this.riskRecommendationId = recommendation.id;
      this.riskSuggestion = this.parseSuggestion(recommendation);
      if (!this.riskSuggestion) {
        const status = recommendation.technical_status || "indisponível";
        this.notifyError(`A IA não retornou os dados de risco (status: ${status}).`);
      }
    },
    pollRecommendation(id, attempt) {
      if (attempt >= COPILOT_POLL_MAX_ATTEMPTS) {
        this.copilot.loading = false;
        this.copilot.busy = false;
        this.notifyError(
          "A cotação está demorando mais que o esperado. Tente novamente em alguns minutos."
        );
        return;
      }
      this.copilotPollTimer = setTimeout(() => {
        this.getCopilotRecommendation({ recommendation_id: id })
          .then((resp) => {
            if (resp.data.status === "processing") {
              this.pollRecommendation(id, attempt + 1);
              return;
            }
            this.handleRecommendation(resp.data);
          })
          // Falha pontual de rede não cancela a espera — tenta no próximo tick.
          .catch(() => this.pollRecommendation(id, attempt + 1));
      }, COPILOT_POLL_INTERVAL_MS);
    },
    decideRisk(action) {
      this.copilot.busy = true;
      return this.decideCopilot({
        recommendation_id: this.riskRecommendationId,
        action,
        rejection_reason:
          action === "reject" ? this.riskRejectionReason : undefined,
      })
        .then(() => this.resetSuggestion())
        .catch(() => this.notifyError("Não foi possível registrar a decisão."))
        .finally(() => {
          this.copilot.busy = false;
        });
    },
    acceptRiskSuggestion() {
      const s = this.riskSuggestion;
      // Necessidades → checkboxes
      Object.entries(NEED_MAP).forEach(([key, field]) => {
        this[field] = this.isTrue(s.necessidades[key]);
      });
      // Renda passiva → campos de valor
      const rp = s.renda_passiva || {};
      if (rp.somatoria_gastos_mensais != null)
        this.monthly_expenses_sum = floatToMoney(rp.somatoria_gastos_mensais);
      if (rp.indenizacao_morte_necessaria != null)
        this.death_indemnity = floatToMoney(rp.indenizacao_morte_necessaria);
      if (rp.custos_novas_protecoes != null)
        this.cost_of_new_protections = floatToMoney(rp.custos_novas_protecoes);
      if (rp.nova_capacidade_poupanca != null)
        this.new_client_savings_amount = floatToMoney(rp.nova_capacidade_poupanca);
      this.saveData();
      this.decideRisk("accept");
    },
    rejectRiskSuggestion() {
      // 1º clique: revela o campo de motivo (rejeição confirmada em confirmRejectRisk).
      this.copilot.rejecting = true;
    },
    confirmRejectRisk() {
      this.decideRisk("reject");
    },
    cancelRejectRisk() {
      this.copilot.rejecting = false;
      this.riskRejectionReason = "";
    },
    regenerateRiskSuggestion() {
      this.copilot.busy = true;
      this.decideCopilot({
        recommendation_id: this.riskRecommendationId,
        action: "regenerate",
      })
        .then((resp) => this.handleRecommendation(resp.data))
        .catch(() => {
          this.copilot.busy = false;
          this.notifyError("Não foi possível uma nova sugestão.");
        });
    },
    editProtection(id) {
      this.openRiskProtectionSidebar({
        id,
        saveAction: () => {
          this.getProtections();
        },
      });
    },
    saveData() {
      const {
        health_monitoring,
        dependent_coverage,
        high_value_asset_protection,
        health_recovery,
        professional_liabilities,
        professional_force_protection,
        real_estate_protection,
        monthly_expenses_sum,
        death_indemnity,
        cost_of_new_protections,
        new_client_savings_amount,
      } = this;

      this.saveRisks({
        id: this.$route.params.id,
        health_monitoring,
        dependent_coverage,
        high_value_asset_protection,
        health_recovery,
        professional_liabilities,
        professional_force_protection,
        real_estate_protection,
        monthly_expenses_sum: moneyToFloat(monthly_expenses_sum),
        death_indemnity: moneyToFloat(death_indemnity),
        cost_of_new_protections: moneyToFloat(cost_of_new_protections),
        new_client_savings_amount: moneyToFloat(new_client_savings_amount),
        customer_id: this.$route.params.id,
      }).catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao salvar as informações. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
    getProtections() {
      this.loading.table = true;
      this.getRisksProtections(this.$route.params.id)
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar as informações. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.table = false;
        });
    },
    deleteProtection(id) {
      this.$swal({
        title: "Tem certeza?",
        text: `Deseja remover a proteção?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.removeRiskProtection({
            id,
            customer_id: this.$route.params.id,
          })
            .then(() => {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Sucesso!",
                  text: "Proteção removida com sucesso.",
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              this.getProtections();
            })
            .catch(() => {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Oops!",
                  text: "Ocorreu um erro ao remover a proteção. Entre em contato com o setor de TI.",
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            });
        }
      });
    },
  },
};
</script>

<style lang="scss">
.financial-planning-step-3 {
  .protection-needs {
    .custom-control-label {
      font-size: 12px !important;
    }
  }
  .boder-col {
    border-top: 1px solid #5a698d;
  }
  .add-new-protection {
    display: grid;
    justify-content: end;
    align-items: center;
    button {
      display: flex;
      align-items: center;
      border-radius: 10px;
      height: 30px;
    }
  }
  .copilot-card {
    min-height: 0 !important;
    height: auto;
    background-color: rgba(115, 103, 240, 0.04);
  }
}
</style>
